import {
  Badge,
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text
} from '@chakra-ui/react';
import { IReviewerRecommendationArticle } from '@publishing-engine/domain/models';
import { IExternalRecommendation } from './types';

interface Props {
  reviewer: IExternalRecommendation | null;
  onClose: () => void;
}

const DetailRow = ({
  icon,
  label,
  value
}: {
  icon?: JSX.Element;
  label: string;
  value?: string | number | null;
}) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Flex align="flex-start" gap={2}>
      {icon ? <Box mt={0.5}>{icon}</Box> : null}
      <Text textStyle="textContext" fontWeight="600" minW={28}>
        {label}
      </Text>
      <Text textStyle="textContext" wordBreak="break-word" flex={1}>
        {value}
      </Text>
    </Flex>
  );
};

const ExternalRecommendationDetailsDrawer = ({ reviewer, onClose }: Props) => {
  const isOpen = reviewer !== null;

  // Relevant articles are provider-specific and live under `extra` in the
  // finalized ReviewerCandidate contract.
  const relevantArticles =
    (reviewer?.extra?.relevantArticles as
      | IReviewerRecommendationArticle[]
      | undefined) ?? [];

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent display="flex" flexDirection="column">
        <DrawerCloseButton onClick={onClose} />
        <DrawerHeader borderBottomWidth="1px">
          <Flex align="center" gap={3} pr={8}>
            <Text textStyle="headingItem">{reviewer?.name ?? '-'}</Text>
            {reviewer?.score !== null && reviewer?.score !== undefined && (
              <Badge variant="success">{reviewer.score.toFixed(2)}</Badge>
            )}
          </Flex>
        </DrawerHeader>

        <DrawerBody overflowY="auto" flex="1">
          <Flex direction="column" gap={3}>
            <DetailRow label="h-Index" value={reviewer?.hIndex} />
            <DetailRow label="Citations" value={reviewer?.citationsCount} />
            <DetailRow label="Total Articles" value={reviewer?.worksCount} />

            {relevantArticles.length > 0 && (
              <>
                <Divider />
                <Text textStyle="headingItem">Relevant Articles</Text>
                <Flex direction="column" gap={2}>
                  {relevantArticles.map(article => (
                    <Flex key={article.id} gap={2} align="flex-start">
                      <Box>
                        <Text textStyle="textContext" wordBreak="break-word">
                          {article.title}
                        </Text>
                        <Text textStyle="headingOverline">{article.year}</Text>
                      </Box>
                    </Flex>
                  ))}
                </Flex>
              </>
            )}

            {reviewer?.concepts && reviewer.concepts.length > 0 && (
              <>
                <Divider />
                <Text textStyle="headingItem">Relevant Concepts</Text>
                <Flex wrap="wrap" gap={2}>
                  {reviewer.concepts.map(concept => (
                    <Badge key={concept} variant="neutral">
                      {concept}
                    </Badge>
                  ))}
                </Flex>
              </>
            )}
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default ExternalRecommendationDetailsDrawer;
